export type EasingFn = (t: number) => number;

export interface ScrollToOptions {
  targetY?: number;
  elementId?: string;
  offset?: number;
  duration?: number;
  delay?: number;
  easing?: EasingFn;
}

export const easeInOutCubic: EasingFn = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

interface animateScrollToOptions {
  targetY: number;
  options: {
    duration?: number;
    easing?: EasingFn;
  };
}

const DEFAULT_OPTIONS = {
  duration: 1000,
  easing: easeInOutCubic,
};

const animateScrollTo = ({
  targetY,
  options = DEFAULT_OPTIONS,
}: Readonly<animateScrollToOptions>): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();

  return new Promise((resolve) => {
    const startY = window.scrollY ?? window.pageYOffset ?? 0;
    const distance = targetY - startY;

    if (distance === 0) {
      resolve();
      return;
    }

    let startTime: number | null = null;

    const step = (now: number) => {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / options.duration!, 1);
      const eased = options.easing!(progress);

      window.scrollTo({ top: startY + distance * eased });

      if (progress < 1) requestAnimationFrame(step);
      else resolve();
    };

    requestAnimationFrame(step);
  });
};

const scrollTo = ({
  targetY,
  elementId,
  offset = 0,
  duration = 800,
  delay = 0,
  easing = easeInOutCubic,
}: ScrollToOptions = {}): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();

  const run = (): Promise<void> => {
    if (typeof targetY === 'number') {
      const safeY = Math.max(0, targetY);
      return animateScrollTo({
        targetY: safeY,
        options: { duration, easing },
      });
    }

    if (elementId) {
      const element = document.getElementById(elementId);
      if (!element) return Promise.resolve();

      const rect = element.getBoundingClientRect();
      const y = Math.max(
        0,
        (window.scrollY ?? window.pageYOffset ?? 0) + rect.top - offset,
      );
      return animateScrollTo({
        targetY: y,
        options: { duration, easing },
      });
    }

    return animateScrollTo({
      targetY: 0,
      options: { duration, easing },
    });
  };

  if (!delay) return run();

  return new Promise((resolve) => {
    setTimeout(() => {
      run().then(resolve);
    }, delay);
  });
};

export default scrollTo;
